import { AbilityId, type CharacterKind } from '@/components'
import { ABILITIES } from '@/game/abilities/definitions'
import { SIGNATURE_LABELS } from '@/game/abilities/signatures'

export interface AbilityHudState {
  charges: number
  maxCharges: number
  /** 0..1 recharge progress toward the next charge; 1 when full. */
  progress: number
  /** Set for a moment after the button was pressed with no charge behind it. */
  denied: boolean
}

export interface FighterHudState {
  name: string
  color: number
  human: boolean
  /** Team id; equals the slot in a free-for-all. */
  team: number
  score: number
  instability: number
  shield: number
  haste: number
  alive: boolean
  /** Screen position of the off-screen marker, or null when on screen. */
  marker: { x: number; y: number } | null
  /** Which fighter this is: decides what the two signature slots are called. */
  kind: CharacterKind
  /** Only populated for human slots; bots show no ability bar. */
  abilities: AbilityHudState[]
}

export interface HudState {
  visible: boolean
  timeLeft: number
  timerWarning: boolean
  winsNeeded: number
  fighters: FighterHudState[]
  announceBig: string
  announceSub: string
  padConnected: boolean
  /** Show the ability explanations (countdown and between rounds). */
  expanded: boolean
  /** Round trip to the game server, online only. */
  ping: number | null
}

const ARC_RADIUS = 25
const ARC_CIRCUMFERENCE = 2 * Math.PI * ARC_RADIUS

interface AbilitySlotEls {
  root: HTMLElement
  arc: SVGCircleElement
  charges: HTMLElement
  key: HTMLElement
  name: HTMLElement
  hint: HTMLElement
}

interface FighterEls {
  card: HTMLElement
  name: HTMLElement
  pips: HTMLElement
  pipEls: HTMLElement[]
  fill: HTMLElement
  value: HTMLElement
  buffs: HTMLElement
  marker: HTMLElement
}

/**
 * In-game HUD, built as DOM over the canvas.
 *
 * Laid out for a free-for-all of up to six fighters rather than a duel: every
 * fighter gets one compact card with their name, score and instability, and the
 * two seats a human can occupy additionally get the ability bar at the bottom
 * corners. Bots deliberately get no ability bar — five more panels of cooldowns
 * nobody controls is noise, not information.
 */
export class Hud {
  readonly root: HTMLElement

  private readonly timerEl: HTMLElement
  private readonly pingEl: HTMLElement
  private readonly rosterEl: HTMLElement
  private readonly announce: HTMLElement
  private readonly announceBig: HTMLElement
  private readonly announceSub: HTMLElement
  private readonly abilityBars: HTMLElement[] = []
  private readonly slots: AbilitySlotEls[][] = []
  private fighters: FighterEls[] = []
  private renderedRoster = ''
  private renderedWins = -1

  constructor(parent: HTMLElement) {
    this.root = el('div', 'hud')

    const top = el('div', 'hud-top')
    this.rosterEl = el('div', 'roster')
    this.timerEl = el('div', 'timer')
    this.timerEl.textContent = '0:00'
    // Timer above the roster: with six cards the roster is two rows deep, and a
    // timer underneath them sat squarely on the playfield.
    top.append(this.timerEl, this.rosterEl)
    this.root.append(top)
    // Online only: when something feels late, this says whether it is the line.
    this.pingEl = el('div', 'ping hidden')
    this.root.append(this.pingEl)

    // Two ability bars: the keyboard seat on the left, the gamepad seat on the
    // right. They are shown or hidden depending on how many humans are playing.
    for (const side of ['left', 'right'] as const) {
      const bar = el('div', `abilities ${side}`)
      const slots: AbilitySlotEls[] = []
      for (const def of ABILITIES) {
        const slot = el('div', 'ability')

        const dial = el('div', 'ability-dial')
        const arcSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
        arcSvg.setAttribute('class', 'ability-arc')
        arcSvg.setAttribute('viewBox', '0 0 56 56')
        const track = circle('arc-track')
        const arc = circle('arc-fill')
        arc.setAttribute('stroke-dasharray', String(ARC_CIRCUMFERENCE))
        arc.setAttribute('stroke-dashoffset', '0')
        arcSvg.append(track, arc)

        const key = el('div', 'ability-key')
        const charges = el('div', 'ability-charges')
        dial.append(arcSvg, key, charges)

        const text = el('div', 'ability-text')
        const name = el('div', 'ability-name')
        name.textContent = def.name
        const hint = el('div', 'ability-hint')
        hint.textContent = def.description
        text.append(name, hint)

        slot.append(dial, text)
        bar.append(slot)
        slots.push({ root: slot, arc, charges, key, name, hint })
      }
      this.root.append(bar)
      this.abilityBars.push(bar)
      this.slots.push(slots)
    }

    this.announce = el('div', 'announce hidden')
    this.announceBig = el('div', 'announce-big')
    this.announceSub = el('div', 'announce-sub')
    this.announce.append(this.announceBig, this.announceSub)
    this.root.append(this.announce)

    parent.append(this.root)
  }

  update(state: HudState): void {
    this.root.classList.toggle('visible', state.visible)
    if (!state.visible) return

    const seconds = Math.max(0, Math.ceil(state.timeLeft))
    this.timerEl.textContent = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
    this.timerEl.classList.toggle('warning', state.timerWarning)
    this.pingEl.classList.toggle('hidden', state.ping === null)
    if (state.ping !== null) {
      this.pingEl.textContent = `ПИНГ ${Math.round(state.ping)} мс`
      this.pingEl.classList.toggle('warning', state.ping > 180)
    }

    // The roster is rebuilt only when the line-up itself changes.
    const signature = `${state.winsNeeded}|${state.fighters.map((f) => f.name).join(',')}`
    if (signature !== this.renderedRoster || state.winsNeeded !== this.renderedWins) {
      this.buildRoster(state)
      this.renderedRoster = signature
      this.renderedWins = state.winsNeeded
    }

    for (let i = 0; i < state.fighters.length && i < this.fighters.length; i++) {
      this.updateFighter(this.fighters[i], state.fighters[i])
    }

    const humans = state.fighters.filter((f) => f.human)
    for (let bar = 0; bar < this.abilityBars.length; bar++) {
      const fighter = humans[bar]
      this.abilityBars[bar].classList.toggle('hidden', fighter === undefined)
      this.abilityBars[bar].classList.toggle('compact', !state.expanded)
      if (!fighter) continue

      const hex = `#${fighter.color.toString(16).padStart(6, '0')}`
      const slots = this.slots[bar]
      for (let i = 0; i < slots.length; i++) {
        const ability = fighter.abilities[i]
        const slot = slots[i]
        slot.root.style.color = hex
        slot.key.textContent =
          bar === 0 ? ABILITIES[i].keyboardLabel : ABILITIES[i].padLabel
        // The two signature slots are named after what *this* fighter has there.
        const label =
          SIGNATURE_LABELS[fighter.kind]?.[ABILITIES[i].id as AbilityId] ?? ABILITIES[i]
        if (slot.name.textContent !== label.name) {
          slot.name.textContent = label.name
          slot.hint.textContent = label.description
        }
        if (!ability) continue
        const ready = ability.charges > 0
        slot.root.classList.toggle('empty', !ready)
        // Answers a press that had nothing behind it. Retriggering the CSS
        // animation needs the class off for one layout, or a second press
        // during the same cooldown flashes nothing.
        if (ability.denied !== slot.root.classList.contains('denied')) {
          slot.root.classList.toggle('denied', ability.denied)
          if (ability.denied) void slot.root.offsetWidth
        }
        slot.arc.setAttribute(
          'stroke-dashoffset',
          String(ARC_CIRCUMFERENCE * (1 - ability.progress)),
        )
        slot.charges.textContent =
          ability.maxCharges > 1 ? String(ability.charges) : ''
      }
    }

    const hasText = state.announceBig !== '' || state.announceSub !== ''
    this.announce.classList.toggle('hidden', !hasText)
    if (hasText) {
      this.announceBig.textContent = state.announceBig
      this.announceSub.textContent = state.announceSub
    }
  }

  private updateFighter(els: FighterEls, data: FighterHudState): void {
    const percent = Math.round(data.instability)
    els.value.textContent = `${percent}%`
    els.fill.style.width = `${percent}%`
    els.fill.style.backgroundColor = instabilityColor(percent)
    els.card.classList.toggle('critical', percent >= 70)
    els.card.classList.toggle('out', !data.alive)

    for (let i = 0; i < els.pipEls.length; i++) {
      els.pipEls[i].classList.toggle('filled', i < data.score)
    }

    renderBuffs(els.buffs, data)

    if (data.marker) {
      els.marker.style.display = 'block'
      els.marker.style.left = `${data.marker.x}px`
      els.marker.style.top = `${data.marker.y}px`
    } else {
      els.marker.style.display = 'none'
    }
  }

  private buildRoster(state: HudState): void {
    this.rosterEl.replaceChildren()
    for (const els of this.fighters) els.marker.remove()
    this.fighters = []

    for (const fighter of state.fighters) {
      const hex = `#${fighter.color.toString(16).padStart(6, '0')}`
      const card = el('div', 'fighter-card')
      card.style.color = hex

      const head = el('div', 'fighter-head')
      const name = el('div', 'fighter-name')
      name.textContent = fighter.name
      const pips = el('div', 'pips')
      const pipEls: HTMLElement[] = []
      for (let i = 0; i < state.winsNeeded; i++) {
        const pip = el('div', 'pip')
        pips.append(pip)
        pipEls.push(pip)
      }
      head.append(name, pips)

      const track = el('div', 'meter-track')
      const fill = el('div', 'meter-fill')
      track.append(fill)

      const value = el('div', 'meter-value')
      value.textContent = '0%'
      const buffs = el('div', 'buffs')

      card.append(head, track, value, buffs)
      this.rosterEl.append(card)

      const marker = el('div', 'marker')
      marker.style.color = hex
      this.root.append(marker)

      this.fighters.push({ card, name, pips, pipEls, fill, value, buffs, marker })
    }
  }
}

/** Renders the active pickup effects as labelled chips with a countdown. */
function renderBuffs(row: HTMLElement, data: FighterHudState): void {
  const active: Array<[string, string, number]> = []
  if (data.shield > 0) active.push(['ЩИТ', 'shield', data.shield])
  if (data.haste > 0) active.push(['РАЗГОН', 'haste', data.haste])

  const signature = active.map(([, kind]) => kind).join(',')
  if (row.dataset.signature !== signature) {
    row.dataset.signature = signature
    row.replaceChildren()
    for (const [label, kind] of active) {
      const chip = el('div', `buff ${kind}`)
      const name = el('span', 'buff-name')
      name.textContent = label
      const time = el('span', 'buff-time')
      chip.append(name, time)
      row.append(chip)
    }
  }

  const chips = row.children
  for (let i = 0; i < active.length && i < chips.length; i++) {
    const time = chips[i].querySelector('.buff-time')
    if (time) time.textContent = `${Math.ceil(active[i][2])}с`
  }
}

function instabilityColor(percent: number): string {
  if (percent < 35) return '#8fff3c'
  if (percent < 70) return '#ffc23c'
  return '#ff3a1a'
}

function circle(className: string): SVGCircleElement {
  const node = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
  node.setAttribute('class', className)
  node.setAttribute('cx', '28')
  node.setAttribute('cy', '28')
  node.setAttribute('r', String(ARC_RADIUS))
  return node
}

export function el(tag: string, className = ''): HTMLElement {
  const node = document.createElement(tag)
  if (className) node.className = className
  return node
}

/** A centred row of controls. */
export function row(...nodes: HTMLElement[]): HTMLElement {
  const node = el('div', 'row')
  node.append(...nodes)
  return node
}

/** A plain block around a group of nodes. */
export function wrap(...nodes: HTMLElement[]): HTMLElement {
  const node = el('div')
  node.append(...nodes)
  return node
}
